import { TYPE } from '@kontentino/kontentino-constants/Pages';

/**
 * The page types whose composer offers the `@` typeahead.
 *
 * This list has to stay a subset of what the backend can actually resolve.
 * `TYPE.FACEBOOK_AD` (101) was in it and is absent from the api's
 * `Page::pageTypeToSocialNetworkMap()`, so a search from an ad account
 * resolved to the literal network string `unknown` with reason
 * `misconfigured` -- surfacing as "Tagging on unknown isn't available right
 * now." Neither repo's tests could see it: each side covers its own
 * enumeration and nothing compared the two.
 */
export const PAGE_MENTION_PAGE_TYPES: number[] = [
  TYPE.FACEBOOK,
  TYPE.LINKEDIN,
  TYPE.TWITTER,
];

/**
 * The page types whose composer offers the direct-mention dialog — paste a URL
 * or slug, get one mention.
 *
 * Kept beside `PAGE_MENTION_PAGE_TYPES` and subject to the same rule: both are
 * gated by `Page::pageTypeToSocialNetworkMap()` on the api side, so anything
 * listed here that the map cannot resolve produces the literal network
 * `unknown`. LinkedIn joins Facebook here in phase 6; ad accounts are excluded
 * for the same reason they were dropped from the typeahead.
 *
 * Instagram is direct-mention only, deliberately absent from
 * `PAGE_MENTION_PAGE_TYPES` above: no Instagram route offers prefix matching
 * over accounts at any tier, so a typeahead there would promise something the
 * backend cannot serve — the same defect that once affected Facebook ad
 * accounts. X (Twitter) has no such restriction and is offered both ways.
 *
 * Instagram has no mention NODE registered either (see
 * `getTextEditorExtensions` — the node is gated on `PAGE_MENTION_PAGE_TYPES`,
 * not this list), so anything inserted for an Instagram page must be plain
 * text: see `HANDLE_AS_LABEL_PAGE_TYPES` and `DirectMention`.
 */
export const DIRECT_MENTION_PAGE_TYPES: number[] = [
  TYPE.FACEBOOK,
  TYPE.LINKEDIN,
  TYPE.INSTAGRAM,
  TYPE.TWITTER,
];

/**
 * Instagram and X resolve `@handle` themselves at publish time -- the
 * mention is plain text on those networks, not an opaque id/URN -- so the
 * inserted label must be the handle, not the display name `name` carries for
 * every other network. Facebook and LinkedIn are untouched: their behaviour
 * stays byte-identical (label = name).
 *
 * This is also, not coincidentally, exactly the set of networks with no
 * mention NODE in the schema (Instagram never had one; X's node exists only
 * because it is also in `PAGE_MENTION_PAGE_TYPES`) -- there is no opaque id
 * to carry for either, which is the entire reason the mention node exists for
 * Facebook and LinkedIn in the first place.
 */
export const HANDLE_AS_LABEL_PAGE_TYPES: number[] = [
  TYPE.INSTAGRAM,
  TYPE.TWITTER,
];
